import { useContext, useEffect, useRef, useState } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider, { Settings } from 'react-slick';
import './image-gallery.scss';
import { Modal } from '../../../components/modal/modal';

export const ProductImageGallery = ({
  images,
}: {
  images: { productId: number; id: string; rank: number; altTitle?: string }[];
}) => {
  const { tenant } = useContext(TenantContext);
  const bucketEnv =
    window.location.host === 'dev.quipli.com' || window.location.host.includes('localhost')
      ? 'dev'
      : window.location.host === 'qa.quipli.com'
      ? 'qa'
      : window.location.host === 'integration.quipli.com'
      ? 'integration'
      : window.location.host === 'beta.quipli.com'
      ? 'beta'
      : tenant === 'gameready'
      ? 'hipaa'
      : 'prod';
  const imageUrl = (productId: number, imageId: string) =>
    `https://quipli-service-${bucketEnv}-public.s3.amazonaws.com/${tenant}/products/${productId}/images/${imageId}/ORIGINAL`;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewFullScreen, setViewFullScreen] = useState(false);
  const thumbSlider = useRef<Slider | null>();
  const mainImageSlider = useRef<Slider | null>();

  const mainSliderSettings: Settings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    lazyLoad: 'ondemand',
    rows: 1,
    speed: 500,
    waitForAnimate: false,
    cssEase: 'ease-out',
    fade: true,
    dots: false,
    arrows: images.length > 1,
    draggable: true,
    infinite: false,
    focusOnSelect: true,
    beforeChange: (currentSlide, nextSlide) => {
      setCurrentSlide(nextSlide);
    },
    responsive: [
      {
        breakpoint: 488,
        settings: {
          dots: true,
        },
      },
    ],
  };

  const thumbSliderSettings: Settings = {
    centerMode: false,
    rows: 1,
    slidesToShow: images.length > 5 ? 5 : images.length,
    slidesToScroll: images.length > 5 ? 5 : images.length,
    arrows: images.length > 5,
    dots: false,
    lazyLoad: 'ondemand',
    swipeToSlide: false,
    draggable: false,
    focusOnSelect: false,
  };

  useEffect(() => {
    thumbSlider.current?.slickGoTo(
      currentSlide > 1 && currentSlide < images.length - 2 ? currentSlide - 2 : currentSlide <= 1 ? 0 : currentSlide,
    );
  }, [currentSlide]);

  return (
    <div className="quipli-v2 image-gallery-wrapper">
      <Modal isOpen={viewFullScreen} close={() => setViewFullScreen(false)}>
        <img src={imageUrl(images[currentSlide]?.productId, images[currentSlide]?.id)} className="full-screen-image" />
      </Modal>
      <div className="image-gallery relative pb-4">
        <Slider
          className="main-image-slider"
          ref={(slider) => (mainImageSlider.current = slider)}
          {...mainSliderSettings}
        >
          {images.length === 0 ? (
            <div className="main-image" key="placeholder">
              <Image
                className="object-contain align-middle justify-center"
                key={'placeholder'}
                imageId={'placeholder'}
                productId={0}
                profile="ORIGINAL"
                loading="eager"
              />
            </div>
          ) : (
            images.map((image, i) => {
              return (
                <div className="main-image" key={image.id}>
                  <Image
                    className="object-contain align-middle justify-center"
                    key={image.id}
                    imageId={image.id}
                    productId={image.productId}
                    alt={image.altTitle}
                    profile="ORIGINAL"
                    onClick={() => setViewFullScreen(true)}
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </div>
              );
            })
          )}
        </Slider>
      </div>
      <Slider
        className="thumbnail-slider px-10"
        ref={(slider) => (thumbSlider.current = slider)}
        {...thumbSliderSettings}
        infinite={false}
      >
        {images.map((image, idx) => (
          <div
            key={image.id}
            className={`thumbnail-slider-img px-2 ${currentSlide === idx ? 'active' : ''}`}
            onClick={() => {
              mainImageSlider.current?.slickGoTo(idx);
              setCurrentSlide(idx);
            }}
          >
            <Image
              className="border-solid border-grey-300 rounded-md cursor-pointer"
              key={image.id}
              imageId={image.id}
              productId={image.productId}
              alt={image.altTitle}
              profile="THUMB"
            />
          </div>
        ))}
      </Slider>
    </div>
  );
};

const Image = ({
  className,
  imageId,
  productId,
  alt,
  profile,
  includeHref,
  onClick,
  loading = 'lazy',
}: {
  className?: string;
  imageId: string;
  productId: number;
  alt?: string;
  profile?: string;
  includeHref?: boolean;
  onClick?: () => void;
  loading?: 'eager' | 'lazy' | undefined;
}) => {
  const { tenant } = useContext(TenantContext);
  const bucketEnv =
    window.location.host === 'dev.quipli.com' || window.location.host.includes('localhost')
      ? 'dev'
      : window.location.host === 'qa.quipli.com'
      ? 'qa'
      : window.location.host === 'integration.quipli.com'
      ? 'integration'
      : window.location.host === 'beta.quipli.com'
      ? 'beta'
      : tenant === 'gameready'
      ? 'hipaa'
      : 'prod';
  const [imageSrc, setImageSrc] = useState<string>(
    imageId !== 'placeholder'
      ? `https://quipli-service-${bucketEnv}-public.s3.amazonaws.com/${tenant}/products/${productId}/images/${imageId}/${profile}`
      : `https://quipli-service-${bucketEnv}-public.s3.amazonaws.com/${tenant}/products/placeholder/${profile}`,
  );

  return includeHref ? (
    <a
      className=" thumbnail items-center relative"
      href={imageSrc}
      data-fancybox="image-gallery"
      data-width={1024}
      data-caption="uyfuyf"
      tabIndex={0}
    >
      <img
        className={`${className}`}
        loading={loading}
        src={imageSrc}
        alt={alt}
        onError={() =>
          setImageSrc(
            `https://quipli-service-${bucketEnv}-public.s3.amazonaws.com/${tenant}/products/placeholder/${profile}`,
          )
        }
      />
    </a>
  ) : (
    <img
      className={`${className}`}
      loading={loading}
      src={imageSrc}
      alt={alt}
      onError={() =>
        setImageSrc(
          `https://quipli-service-${bucketEnv}-public.s3.amazonaws.com/${tenant}/products/placeholder/${profile}`,
        )
      }
      onClick={onClick}
    />
  );
};
